import { useId, useRef, useState, type DragEvent, type ReactNode } from 'react';

import { PageGlyph } from '@/components/ui';
import { cn } from '@/lib/cn';

export interface DropzoneProps {
  /** Called with the first file the customer picked or dropped. */
  onFile: (file: File) => void;
  /** `accept` attribute, e.g. ".pdf,application/pdf" — built from meta, not hardcoded. */
  accept: string;
  /** Helper line under the button: what we take and how big. */
  hint?: ReactNode;
  disabled?: boolean;
  className?: string;
}

/**
 * Tap to choose, or drag and drop.
 *
 * The control is a real `<input type="file">` with a real `<label>`, visually
 * hidden but NOT removed from the tab order. That single decision buys the whole
 * accessibility story: the label is a 44px+ tap target that opens the picker on
 * touch, the input is focusable and opens the picker on Enter/Space, the file
 * name is announced by the platform, and there is no `role="button"` div
 * pretending to be a control.
 *
 * Drag and drop is layered on top as an ENHANCEMENT and is never the only way in
 * — a phone has no drag source, and the brief asks for both.
 */
export function Dropzone({ onFile, accept, hint, disabled = false, className }: DropzoneProps) {
  const inputId = useId();
  const hintId = `${inputId}-hint`;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  // Nested dragenter/dragleave pairs fire for every child element, so a boolean
  // flips off the moment the pointer crosses an inner node. Counting depth is
  // the standard fix.
  const dragDepth = useRef(0);

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    // Without preventDefault the browser navigates to the dropped file instead.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    const file = event.dataTransfer.files.item(0);
    if (file) onFile(file);
  };

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn('relative', className)}
      data-testid="dropzone"
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        disabled={disabled}
        aria-describedby={hint ? hintId : undefined}
        className="peer sr-only"
        onChange={(event) => {
          const file = event.target.files?.item(0);
          // Reset first: picking the same file twice in a row must still fire a
          // change event, which it will not if the value is unchanged.
          event.target.value = '';
          if (file) onFile(file);
        }}
      />
      <label
        htmlFor={inputId}
        className={cn(
          'flex cursor-pointer flex-col items-center gap-3 rounded-card border-2 border-dashed px-4 py-8 text-center',
          'transition-colors duration-150',
          'peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-focus peer-focus-visible:ring-offset-2',
          dragging ? 'border-brand-ink bg-brand-soft' : 'border-line-strong bg-surface',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <PageGlyph className="h-10 w-10 text-ink-subtle" />
        <span className="text-base font-semibold text-ink">
          {dragging ? 'Drop your file here' : 'Choose the file to print'}
        </span>
        {/* Looks like the primary button and is inside the label, so the whole
            card is one tap target rather than a button next to a drop area. */}
        <span className="inline-flex min-h-touch items-center justify-center rounded-control bg-brand px-5 font-semibold text-brand-contrast">
          Choose a file
        </span>
        <span className="text-sm text-ink-muted">or drag it here from your computer</span>
      </label>
      {hint ? (
        <p id={hintId} className="mt-2 text-center text-sm text-ink-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
